import classNames from 'classnames';
import Button from '@rio-cloud/rio-uikit/Button';
import NoData from '@rio-cloud/rio-uikit/NoData';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import Table, {
    TableBatchDropdown,
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    type TableRowId,
    type TableSortDirection,
    type TableViewType,
} from '@rio-cloud/rio-uikit/Table';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AssetDevices } from './AssetDevices';
import AssetIdentification from './AssetIdentification';
import { AssetSharingCell } from './AssetSharingCell';
import { AssetSharingsDialog } from './AssetSharingsDialog';
import { AssetTags } from './AssetTags';
import AssetType from './AssetType';
import { formatLicensePlateForDisplay } from './CsvExport.helper';
import { isAssetTableColumnSortable } from './assetsUtils';
import { AssetStatus, type Asset, type AssetListProperties, type AssociatedDevice, type Tag } from './types';

const getVehicleStatusLabelClassName = (status: AssetStatus) =>
    classNames('label label-condensed', status === AssetStatus.active ? 'label-primary' : 'label-muted');

const SEARCH_HIGHLIGHT_CLASS = 'search-result-highlight';
const SEARCH_IGNORE_SELECTOR = '[data-search-ignore="true"]';

const isSearchHighlightSupported = () =>
    typeof window !== 'undefined' && 'CSS' in window && 'highlights' in CSS && typeof Highlight !== 'undefined';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const collectSearchHighlightRanges = (root: HTMLElement, query: string): Range[] => {
    const ranges: Range[] = [];

    if (!query) {
        return ranges;
    }

    const regex = new RegExp(escapeRegExp(query), 'gi');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
            const parentElement = node.parentElement;

            if (!parentElement || parentElement.closest(SEARCH_IGNORE_SELECTOR)) {
                return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
        },
    });

    let node: Node | null = walker.nextNode();

    while (node) {
        const text = node.textContent ?? '';
        regex.lastIndex = 0;

        let match: RegExpExecArray | null = regex.exec(text);

        while (match) {
            const range = document.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            ranges.push(range);

            match = regex.exec(text);
        }

        node = walker.nextNode();
    }

    return ranges;
};

const renderNoData = () => (
    <span data-search-ignore='true'>
        <NoData text={<FormattedMessage id='intl-msg:common-message.noData' />} />
    </span>
);

export const AssetsList = (props: AssetListProperties) => {
    const intl = useIntl();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [assetForSharingDetails, setAssetForSharingDetails] = useState<Asset | null>(null);

    const renderVehicleColumnValue = (asset: Asset) => {
        const mutedClassName = asset.status === AssetStatus.archived ? 'text-color-gray' : undefined;

        return (
            <div className={classNames('display-flex align-items-center gap-10', mutedClassName)}>
                <AssetType assetType={asset.type} fuelType={asset.masterdata?.fuel_type} className={mutedClassName} />
                <div className='display-flex flex-column'>
                    <div className='line-height-125rel ellipsis-1 text-medium' title={asset.name}>
                        {asset.name}
                    </div>
                    {asset.identification && (
                        <div className='margin-top-2'>
                            <AssetIdentification
                                assetIdentification={asset.identification}
                                assetIdentificationType={asset.identification_type}
                                showIdentificationType
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderColumnValue = (column: string, asset: Asset, tags: Tag[], devices: AssociatedDevice[]) => {
        switch (column) {
            case 'name': {
                return asset.name.trim() ? asset.name : renderNoData();
            }
            case 'type': {
                return intl.formatMessage({ id: `intl-msg:asset.type.${asset.type}` });
            }
            case 'identification': {
                return (
                    <span data-search-ignore={!asset.identification?.trim() || undefined}>
                        <AssetIdentification assetIdentification={asset.identification} />
                    </span>
                );
            }
            case 'status': {
                return (
                    <span className={getVehicleStatusLabelClassName(asset.status)}>
                        {intl.formatMessage({ id: `intl-msg:asset.status.${asset.status}` })}
                    </span>
                );
            }
            case 'vehicle': {
                return renderVehicleColumnValue(asset);
            }
            case 'brand': {
                return asset.brand?.trim() ? asset.brand : renderNoData();
            }
            case 'fuel_type': {
                return asset.masterdata?.fuel_type
                    ? intl.formatMessage({ id: `intl-msg:masterdata.fuel-type.${asset.masterdata.fuel_type}` })
                    : renderNoData();
            }
            case 'license_plate': {
                const licensePlate = formatLicensePlateForDisplay(asset);

                return licensePlate ? licensePlate : renderNoData();
            }
            case 'tags': {
                const matchingTags = tags.filter(tag => asset.tags.includes(tag.id));

                return (
                    <span data-search-ignore={matchingTags.length === 0 || undefined}>
                        <AssetTags icon='group' assetTags={asset.tags} tags={tags} />
                    </span>
                );
            }
            case 'devices': {
                const assetDevices = devices === undefined ? [] : devices;

                return (
                    <span data-search-ignore={assetDevices.length === 0 || undefined}>
                        <AssetDevices assetDevices={assetDevices} />
                    </span>
                );
            }
            case 'sharings': {
                return (
                    <span data-search-ignore='true'>
                        <AssetSharingCell asset={asset} onShowDetails={setAssetForSharingDetails} />
                    </span>
                );
            }
            default:
                throw Error('column from ColumnConfig not handled in renderColumnValue - please add it');
        }
    };

    const visibleColumns = props.columns.filter(column => column.visible);
    const translatedColumns = visibleColumns.map(column => ({
        ...column,
        translatedLabel: intl.formatMessage({ id: column.label }),
    }));

    const rows: Asset[] = props.assets;
    const tableViewType = props.tableViewType as TableViewType;

    useEffect(() => {
        if (!isSearchHighlightSupported()) {
            return;
        }

        const tableBody = tableContainerRef.current?.querySelector('.table-body') as HTMLElement | null;

        if (!tableBody) {
            return;
        }

        const highlight = CSS.highlights.get(SEARCH_HIGHLIGHT_CLASS) ?? new Highlight();
        CSS.highlights.set(SEARCH_HIGHLIGHT_CLASS, highlight);

        const query = props.searchString?.trim() ?? '';
        const raf = window.requestAnimationFrame(() => {
            highlight.clear();

            for (const range of collectSearchHighlightRanges(tableBody, query)) {
                highlight.add(range);
            }
        });

        return () => {
            window.cancelAnimationFrame(raf);
            highlight.clear();
        };
    }, [props.searchString, rows, translatedColumns, tableViewType]);

    const dropdownButtonItems = [
        {
            value: (
                <div data-cy='assets-table-multiselect-archive-button'>
                    <span className='uikit-icon icon-inbox-in margin-right-10 text-size-18' />
                    <FormattedMessage id={'intl-msg:archiveSelected'} />{' '}
                    {props.selectedAssetIds.length > 0 && `(${props.selectedAssetIds.length})`}
                </div>
            ),
            disabled: props.selectedAssetIds.length === 0,
            onSelect: () => props.showBulkArchiveDialog(true),
        },
        {
            value: (
                <div data-cy='assets-table-multiselect-add-to-group-button'>
                    <span className='uikit-icon icon-plus-sign margin-right-10 text-size-18' />
                    <FormattedMessage id={'intl-msg:addToGroup'} />{' '}
                    {props.selectedAssetIds.length > 0 && `(${props.selectedAssetIds.length})`}
                </div>
            ),
            disabled: props.selectedAssetIds.length === 0,
            onSelect: () => props.showAddToGroupDialog(true),
        },
        {
            value: (
                <div data-cy='assets-table-multiselect-remove-from-group-button'>
                    <span className='uikit-icon icon-minus-sign margin-right-10 text-size-18' />
                    <FormattedMessage id={'intl-msg:removeFromGroup'} />{' '}
                    {props.selectedAssetIds.length > 0 && `(${props.selectedAssetIds.length})`}
                </div>
            ),
            disabled: props.selectedAssetIds.length === 0,
            onSelect: () => props.showRemoveFromGroupDialog(true),
        },
        {
            divider: true,
        },
        {
            value: (
                <div data-cy='assets-table-multiselect-select-all-button'>
                    <FormattedMessage id={'intl-msg:selectAll'} />
                </div>
            ),
            onSelect: () => props.handleToggleAll(true),
        },
        {
            value: (
                <div data-cy='assets-table-multiselect-deselect-all-button'>
                    <FormattedMessage id={'intl-msg:deselectAll'} />
                </div>
            ),
            onSelect: () => props.handleToggleAll(false),
        },
    ];

    const handleSelectionChange = (rowIds: TableRowId[]) => {
        props.handleSelectionChange(rowIds.map(String));
    };

    const handleActiveRowChange = (rowId: TableRowId | undefined) => {
        props.handleActiveRowChange(rowId === undefined ? undefined : String(rowId));
    };

    return (
        <div id='AssetsTable' ref={tableContainerRef}>
            {assetForSharingDetails && (
                <AssetSharingsDialog
                    asset={assetForSharingDetails}
                    devices={props.devices[assetForSharingDetails.id] ?? []}
                    onClose={() => setAssetForSharingDetails(null)}
                />
            )}
            {rows.length ? (
                <Table
                    key={`assets-table-${props.tableResetKey}`}
                    rowKey='id'
                    viewType={tableViewType}
                    condensed={props.compactRows ?? false}
                    hover={true}
                    gridLines={props.gridLines ?? false}
                    activeRowId={props.selectedAsset?.id}
                    onActiveRowChange={handleActiveRowChange}
                    showSelectionColumn={true}
                    selectedRowIds={props.selectedAssetIds}
                    onSelectionChange={handleSelectionChange}
                    selectionHeaderContent={
                        <div data-cy='assets-table-multiselect-button'>
                            <TableBatchDropdown
                                items={dropdownButtonItems}
                                dropdownClassName='assets-table-multiselect-menu'
                            />
                        </div>
                    }
                    sortBy={props.sortBy ?? undefined}
                    sortDirection={(props.sortDir as TableSortDirection | null) ?? undefined}
                    onSortChange={columnKey => props.handleSortChange(columnKey)}
                    onColumnResize={props.handleColumnResize}
                    columnOrder={props.columns.map(column => column.key)}
                    onColumnOrderChange={props.handleColumnOrderChange}
                    stickyReference='applicationLayout'
                    responsiveBreakpoint={{ list: '800px' }}
                >
                    <TableHeader sticky={true}>
                        {translatedColumns.map(column => (
                            <TableHeaderColumn
                                key={column.key}
                                columnKey={column.key}
                                label={column.translatedLabel}
                                mobileLabel={column.translatedLabel}
                                width={column.width}
                                draggable={true}
                                resizeable={true}
                                sortable={isAssetTableColumnSortable(column.key, props.columns)}
                                data-field={column.translatedLabel}
                                data-sortby={column.key}
                            >
                                {column.translatedLabel}
                            </TableHeaderColumn>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow
                                key={row.id}
                                id={row.id}
                                data-testid='assets-table-row'
                                data-cy='assets-table-row'
                            >
                                {translatedColumns.map(column => (
                                    <TableColumn
                                        key={column.key}
                                        columnKey={column.key}
                                        data-field={column.translatedLabel}
                                    >
                                        {renderColumnValue(column.key, row, props.tags, props.devices[row.id])}
                                    </TableColumn>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <div className='display-flex justify-content-center margin-top-15pct'>
                    <NotFoundState
                        headline={<FormattedMessage id={'intl-msg:notfound.headline'} />}
                        message={<FormattedMessage id='intl-msg:notfound.message.with-action' />}
                    >
                        <Button
                            bsStyle={Button.PRIMARY}
                            autoTrackingKey='intl-msg:create.not-found-state'
                            id='addAsset-new-not-found-state'
                            onClick={props.onCreateAsset}
                        >
                            <FormattedMessage id='intl-msg:create' />
                        </Button>
                    </NotFoundState>
                </div>
            )}
        </div>
    );
};
